import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'ko/what-is-social-traffic',
    titleTag: '소셜 트래픽이란? 완벽 가이드 2026 | Traffic Creator',
    metaDescription: '소셜 트래픽, 리퍼럴 트래픽, 오가닉 트래픽의 차이를 이해하세요. SEO와 사이트 권위를 높이기 위한 트래픽 소스 최적화 방법을 알아보세요.',
    keywords: '소셜 트래픽이란, 리퍼럴 트래픽 정의, 웹사이트 트래픽 유형, 봇 트래픽 vs 실제 트래픽, 트래픽 가이드',
    primaryKeyword: '소셜 트래픽이란',
    secondaryKeyword: '리퍼럴 트래픽 정의',
    reviewCount: '465',
    ratingValue: '4.9',

    h1: '소셜 트래픽 & 리퍼럴 트래픽이란? — 완벽 가이드',
    subheadline: '리퍼럴 트래픽의 정의와 소셜 트래픽이 웹사이트에 미치는 영향을 이해하고 싶으신가요? 이 가이드가 모든 것을 명확하게 설명해드립니다.',

    problemH2: '트래픽 유형 혼동이 성장을 가로막는 이유',
    problemText: '웹 분석의 핵심은 방문자의 출처를 정확히 파악하는 것입니다. 오가닉 트래픽, 다이렉트 트래픽, 리퍼럴 트래픽, 소셜 트래픽을 혼동하면 마케팅 예산을 잘못 배분하고 장기적인 SEO 전략에 해를 끼칩니다.',
    solutionPoints: [
        '주요 웹사이트 트래픽 유형 명확히 정의: 소셜, 리퍼럴, 다이렉트, 오가닉',
        '봇 트래픽 vs 실제 트래픽이 GA4 데이터에 미치는 영향 이해',
        'Traffic Creator가 소셜 및 리퍼럴 채널을 안전하게 시뮬레이션하는 방법 파악',
        '트래픽 소스의 적절한 다양화가 SEO에 미치는 이점 발견'
    ],

    featuresH2: '웹사이트 트래픽 유형 이해하기',
    features: [
        { icon: <Globe />, title: '소셜 트래픽', description: '소셜 트래픽이란? Facebook, Twitter, Instagram, YouTube 등의 플랫폼에서 방문하는 사용자를 의미합니다. 높은 비율은 강력한 브랜드 존재감을 나타냅니다.' },
        { icon: <Activity />, title: '리퍼럴 트래픽 정의', description: '리퍼럴 트래픽은 검색 엔진과 소셜 네트워크를 제외한 외부 사이트에서 오는 방문입니다. 콘텐츠가 신뢰받고 널리 링크되고 있음을 의미합니다.' },
        { icon: <BarChart3 />, title: '봇 트래픽 vs 실제 트래픽', description: '좋은 봇(Googlebot 등)은 사이트를 크롤링합니다. 나쁜 봇은 분석을 망합니다. 실제 레지덴셜 트래픽은 인간 행동을 안전하게 모방하여 지표를 개선합니다.' }
    ],

    testimonialsH2: 'Traffic Creator에 대한 고객 평가',
    testimonials: [
        { name: 'David M.', role: '디지털 마케터', result: '균형 잡힌 분석', text: '이전에는 100% 다이렉트 트래픽이었습니다. Traffic Creator로 타겟팅된 리퍼럴과 소셜 트래픽을 구매하여 GA4 프로필이 완벽하게 균형을 이루었습니다.', seed: 'davidm' },
        { name: 'Sarah K.', role: 'SEO 에이전시', result: '신뢰도 향상', text: '리퍼럴 트래픽의 정의를 이해한 후 클라이언트에게 다양한 트래픽 소스의 필요성을 설명할 수 있게 되었습니다. Traffic Creator가 바로 그것을 제공합니다.', seed: 'sarahk' },
        { name: 'Jessica T.', role: '블로그 운영자', result: '안전한 트래픽', text: '이 가이드를 읽기 전까지 봇 트래픽이 걱정이었습니다. Traffic Creator의 레지덴셜 IP는 SEO에 정말 안전하고 효과적입니다.', seed: 'jessicat' }
    ],

    ctaH2: '소셜 및 리퍼럴 지표를 개선할 준비가 되셨나요?',

    faqs: [
        { question: '소셜 트래픽이 정확히 무엇이고 왜 필요한가요?', answer: '소셜 트래픽은 소셜 미디어 플랫폼에서 웹사이트를 찾는 방문자를 의미합니다. 검색 엔진이 강력한 소셜 신호를 권위와 인기의 지표로 간주하기 때문에 필요합니다.' },
        { question: '리퍼럴 트래픽의 표준 정의는?', answer: '리퍼럴 트래픽은 사용자가 다른 사이트(검색 엔진 제외)의 링크를 클릭하여 귀하의 사이트에 도달할 때 발생합니다. 사이트가 웹 전반에서 논의되고 있음을 보여줍니다.' },
        { question: '봇 트래픽과 실제 레지덴셜 트래픽의 차이는?', answer: '위험한 봇 트래픽은 데이터센터 IP에서 오며 100% 이탈률을 보입니다. 실제 레지덴셜 트래픽은 가정용 IP를 사용하고, 페이지를 스크롤하고, 링크를 클릭하며, 분석에서 완전히 자연스럽게 보입니다.' },
        { question: 'Traffic Creator로 소셜과 리퍼럴 트래픽을 생성할 수 있나요?', answer: '네! 맞춤 캠페인으로 리퍼러를 설정할 수 있습니다. facebook.com이나 reddit.com 같은 URL을 입력하면 Google Analytics에서 소셜 또는 리퍼럴로 표시됩니다.' },
        { question: '소셜 및 리퍼럴 트래픽 구매는 안전한가요?', answer: '네, 고품질 레지덴셜 IP(Traffic Creator가 사용하는 것)를 사용한다면 안전합니다. 봇 감지기를 우회하고 완전히 자연스럽게 보여서 지표를 개선하면서 사이트를 보호합니다.' }
    ],

    relatedLinks: [
        { label: '소셜 미디어 트래픽 봇', to: '/ko/social-media-traffic-bot' },
        { label: '웹사이트 트래픽 생성기', to: '/ko/website-traffic-generator' },
        { label: '고품질 웹사이트 트래픽', to: '/ko/high-quality-website-traffic' },
    ],

    productName: '소셜 & 리퍼럴 트래픽이란? | 2026 완벽 가이드',
    productDescription: '소셜, 리퍼럴, 오가닉 트래픽의 차이를 이해하세요. 트래픽 소스를 최적화하여 SEO와 사이트 권위를 높이는 방법을 배우세요.',

    translations: {
        navFeatures: '기능', navPricing: '가격', navFaq: 'FAQ', navLogin: '로그인',
        heroBadge: '트래픽 교육 가이드 2026', heroCtaPrimary: '무료 체험', heroCtaSecondary: '가격 보기',
        heroTrust: '10,000+ SEO 전문가가 신뢰', heroMicrocopy: '신용카드 불필요. 100% 안전하고 Analytics 인증.',
        heroLiveLabel: '실시간', heroTrafficIncrease: '트래픽 증가', heroActiveLabel: '활성', heroVisitorsServed: '전송된 방문자',
        socialProofLabel: '신뢰 기업:', problemWithout: '트래픽 다양화 없이', problemWith: 'Traffic Creator 사용 후',
        problemBullets: ['우수한 콘텐츠가 트래픽 다양성 부족으로 묻힘', '단일 트래픽 소스가 SEO 신호를 약화', '예산 배분 실수가 마케팅 자원 낭비', '왜곡된 분석 데이터로 잘못된 결정'],
        statsCampaigns: '일일 활성 캠페인', statsCountries: '커버 국가', statsUptime: '시스템 가동률', statsSupport: '24/7 전문 지원',
        featuresSubtitle: '트래픽 소스, 리퍼럴 채널, 사용자 행동 신호에 대한 완전한 통제.',
        howItWorksH2: '간단한 3단계',
        howItWorksSteps: [
            { title: '캠페인 구성', desc: '대상 URL, 리퍼러 소스, 체류 시간, 지역 타겟팅을 설정하세요.' },
            { title: '시스템 가동', desc: '당사 시스템이 레지덴셜 IP를 통해 진정한 소셜 및 리퍼럴 트래픽을 전송합니다.' },
            { title: '결과 분석', desc: 'Google Analytics에서 트래픽 다양화 효과를 실시간으로 확인하세요.' }
        ],
        pricingH2: '투명한 요금', pricingSubtitle: '일회성 구매. 숨겨진 구독 없음. 크레딧 만료 없음.',
        pricingMostPopular: '가장 인기', pricingVisitors: '방문자/클릭', pricingGetStarted: '시작하기',
        pricingGuarantee: '30일 환불 보장', pricingDelivery: '즉시 활성화 (~5분)', pricingCountries: '190+ 국가 타겟팅',
        faqH2: '자주 묻는 질문',
        ctaSubtitle: '10,000+ 만족 고객과 함께 안전한 소셜 및 리퍼럴 트래픽으로 분석을 최적화하세요.',
        ctaButtonPrimary: '무료로 시작', ctaButtonSecondary: '요금제 보기',
        ctaMicrocopy: '신용카드 불필요. Google Analytics에 100% 감지 불가.',
        footerDescription: '전 세계 SEO 전문가와 디지털 에이전시를 위한 고급 트래픽 솔루션.',
        footerRelatedServices: '관련 서비스', footerCompany: '회사', footerAbout: '소개', footerBlog: '블로그',
        footerHelpCenter: '고객센터', footerPrivacy: '개인정보 보호정책', footerImpressum: '법적 정보',
        footerStatus: '모든 시스템 정상 가동', footerBuyTraffic: '웹사이트 트래픽 구매',
    }
};

const KoWhatIsSocialTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default KoWhatIsSocialTraffic;
